// KpiGrid başlığı (Dalga 3'te KpiGrid.tsx'ten ayrıldı — ≤600 satır kuralı). Toplam sütun grubu YOKKEN
// çıktı eski tek satırlı başlıkla birebir aynıdır (danışman byte-eşit). Grup varken (portal, §12/1)
// iki satır: tüm diğer başlıklar `rowSpan=2`, grup başlığı üst satırda (`colSpan` = yıl sayısı, Σ),
// yıl başlıkları alt satırda (`KpiGridTotalColumns.tsx`). İki satır da sticky: alt satır `top`'u
// üst satırın sabit yüksekliğidir.

import { useEffect, useRef, useState } from "react";
import type { KpiTotalKind } from "@mahir/shared";
import { isTotalColumn, type GridColumn } from "../../lib/kpi-grid-nav";
import { KPI_COPY } from "../../lib/kpi-ui";
import {
  TOTAL_GROUP_ROW_HEIGHT,
  TotalGroupHeader,
  TotalYearHeaders,
  totalGroupColumns,
} from "./KpiGridTotalColumns";

export const TH: React.CSSProperties = {
  position: "sticky",
  top: 0,
  background: "var(--color-surface)",
  padding: "8px 10px",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  borderBottom: "1px solid var(--color-border)",
  textAlign: "right",
  whiteSpace: "nowrap",
  zIndex: 2,
};

export function KpiGridHead(props: {
  theadRef: React.RefObject<HTMLTableSectionElement>;
  columns: GridColumn[];
  totalKind: KpiTotalKind;
  fiscalStartMonth: number;
  narrow: boolean;
  nameColWidth: number;
  showChangeCol: boolean;
  /** portal durum sütunu başlığı */
  statusCol: boolean;
  stickyStatus: boolean;
}): JSX.Element {
  const { columns, narrow, nameColWidth, totalKind } = props;
  const minWidth = narrow ? 88 : 96;
  const totals = totalGroupColumns(columns);
  const grouped = totals.length > 0 && totalKind !== "none";
  const rowSpan = grouped ? 2 : undefined;
  // Birinci satırın GERÇEK yüksekliği: grup etiketi sarılınca (dar ekran, uzun kapsam etiketi) büyür;
  // ikinci satır bu ofsetle yapışır ki etiketin ikinci satırını örtmesin.
  const firstRowRef = useRef<HTMLTableRowElement>(null);
  const [firstRowH, setFirstRowH] = useState(TOTAL_GROUP_ROW_HEIGHT);
  useEffect(() => {
    const el = firstRowRef.current;
    if (!grouped || !el) return;
    const measure = (): void => setFirstRowH(Math.ceil(el.getBoundingClientRect().height));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [grouped, totals.length, narrow]);
  const mainColumns = columns.filter((c) => !isTotalColumn(c));

  const head = (c: GridColumn, i: number): JSX.Element => (
    <th
      key={c.key}
      title={c.title}
      {...(rowSpan ? { rowSpan } : {})}
      style={{
        ...TH,
        minWidth,
        background: c.aggregate ? "var(--color-bg)" : "var(--color-surface)",
        color: i === 0 && c.kind === "year" ? "var(--color-primary)" : "var(--color-text-3)",
      }}
    >
      {c.label}
    </th>
  );

  return (
    <thead ref={props.theadRef}>
      <tr ref={firstRowRef}>
        <th
          {...(rowSpan ? { rowSpan } : {})}
          style={{
            ...TH,
            textAlign: "left",
            position: "sticky",
            left: 0,
            zIndex: 3,
            minWidth: nameColWidth,
            maxWidth: nameColWidth,
          }}
        >
          Gösterge
        </th>
        <th {...(rowSpan ? { rowSpan } : {})} style={{ ...TH, textAlign: "left", minWidth: 80 }}>
          Birim
        </th>
        {mainColumns.map((c, i) => head(c, i))}
        {grouped ? <TotalGroupHeader columns={columns} kind={totalKind} style={TH} /> : null}
        {props.showChangeCol ? (
          <th {...(rowSpan ? { rowSpan } : {})} style={{ ...TH, minWidth: 90 }}>
            Değişim
          </th>
        ) : null}
        {props.statusCol ? (
          // sağa yapışık (yalnız ≥1024): geniş tablolarda durum kolonu kaydırmada görünür kalır
          <th
            {...(rowSpan ? { rowSpan } : {})}
            style={{
              ...TH,
              textAlign: "left",
              minWidth: 180,
              ...(props.stickyStatus ? { position: "sticky", right: 0, zIndex: 3 } : {}),
              borderLeft: "1px solid var(--color-border)",
            }}
          >
            {KPI_COPY.portal.statusCol}
          </th>
        ) : null}
      </tr>
      {grouped ? (
        <tr>
          <TotalYearHeaders
            columns={columns}
            style={TH}
            fiscalStartMonth={props.fiscalStartMonth}
            minWidth={minWidth}
            top={firstRowH}
          />
        </tr>
      ) : null}
    </thead>
  );
}
